/* Xiaomi 2026 · Reusable inline pref editor
 *
 * Renders a value with a small pencil icon. Click → becomes a textarea
 * with Save/Cancel. Save calls PATCH /api/xiaomi/dealers/:dealerId with
 * the dotted field path (e.g. "preferences_2026.weekend_anniversary_dinner")
 * and the header `x-edit-source: inline` so the existing
 * xiaomi_dealer_audit_log automatically records:
 *   { entity_type: "xiaomi_dealer", field, before, after, actor, source }
 *
 * Used in three places that all reflect the same dealer doc:
 *   1. Ayush row expanded panel · click-to-edit any pref in place
 *   2. Ayush "Edit options" modal · all 6 prefs in one form
 *   3. MessageComposerModal · editable strip above the source textarea
 *
 * All three call `onSaved(newValue)` so the parent can refresh its
 * local state (or trigger a load()) to keep the surfaces in sync.
 */
import { useState } from 'react';
import axios from 'axios';
import { Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'x-edit-source': 'inline',
  },
});


export default function EditablePref({
  value,
  label,
  dealerId,
  fieldPath,
  onSaved,
  multiline = true,
  placeholder = '',
  testId,
  highlight = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [busy, setBusy] = useState(false);

  const startEdit = () => {
    setDraft(value || '');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value || '');
  };

  const save = async () => {
    const next = (draft || '').trim();
    const before = (value || '').trim();
    if (next === before) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await axios.patch(
        `${API}/xiaomi/dealers/${encodeURIComponent(dealerId)}`,
        { [fieldPath]: next },
        authHdr(),
      );
      toast.success(`${label} updated · audit logged`);
      setEditing(false);
      onSaved?.(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const labelColor = highlight ? '#9A4B0E' : '#6b7280';

  if (!editing) {
    return (
      <div className="group flex items-start gap-2" data-testid={testId}>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] uppercase tracking-wider font-semibold"
            style={{ color: labelColor }}
          >
            {label}
          </div>
          <div className="text-[12px] text-[#15110d] whitespace-pre-wrap break-words">
            {value || <span className="italic text-soft">— empty —</span>}
          </div>
        </div>
        <button
          onClick={startEdit}
          className="opacity-50 group-hover:opacity-100 p-1 rounded hover:bg-[#fef3c7] transition-opacity"
          style={{ color: '#9A4B0E' }}
          title="Edit (audit-logged)"
          data-testid={testId ? `${testId}-edit-btn` : undefined}
        >
          <Pencil size={11} />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid={testId}>
      <div
        className="text-[10px] uppercase tracking-wider font-semibold"
        style={{ color: labelColor }}
      >
        {label}
      </div>
      {multiline ? (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          rows={2}
          autoFocus
          className="w-full px-2 py-1.5 border rounded text-[12px] focus:outline-none focus:border-[#ff6900]"
          style={{ borderColor: '#fde68a' }}
          data-testid={testId ? `${testId}-input` : undefined}
        />
      ) : (
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          autoFocus
          className="w-full px-2 py-1.5 border rounded text-[12px] focus:outline-none focus:border-[#ff6900]"
          style={{ borderColor: '#fde68a' }}
          data-testid={testId ? `${testId}-input` : undefined}
        />
      )}
      <div className="flex gap-1">
        <button
          onClick={save}
          disabled={busy}
          className="px-2 py-0.5 rounded text-[11px] text-white font-semibold disabled:opacity-50 flex items-center gap-1"
          style={{ background: '#16a34a' }}
          data-testid={testId ? `${testId}-save` : undefined}
        >
          <Check size={11} /> Save
        </button>
        <button
          onClick={cancel}
          disabled={busy}
          className="px-2 py-0.5 rounded text-[11px] border bg-white flex items-center gap-1"
          data-testid={testId ? `${testId}-cancel` : undefined}
        >
          <X size={11} /> Cancel
        </button>
      </div>
    </div>
  );
}
